import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../base/icons/components/Icon';
import { IconAI, IconCloseLarge, IconSearch } from '../../../base/icons/svg';
import { setOpen, useSiInviteState } from '../../store';
import PresentStrip from './PresentStrip';
import SiCard from './SiCard';
import { SI_INVITE_CSS } from './styles';
import useWords from './useWords';
/**
 * Kamo Meet "Invite an SI" (owner order 2026-10-26): the SIs this signed-in member may bring into the meeting, grouped
 * "You supervise" and "Available to you", each with its face, job title and availability; an SI already here (or on
 * its way) is never offered. Follows each invite through to "Joined", or says in plain words why it did not work.
 * Org brand colours come from the theme's CSS variables; motion stops under prefers-reduced-motion.
 *
 * @returns {ReactElement|null}
 */
export default function SiInvitePanel() {
    const s = useSiInviteState();
    const w = useWords();
    const [query, setQuery] = useState('');
    const panelRef = useRef(null);
    const searchRef = useRef(null);
    const onClose = useCallback(() => setOpen(false), []);
    const onQuery = useCallback((e) => setQuery(e.target.value), []);
    useEffect(() => {
        if (!s.open) {
            return;
        }
        const onKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        const focus = setTimeout(() => (searchRef.current ?? panelRef.current)?.focus(), 60);
        return () => {
            window.removeEventListener('keydown', onKey);
            clearTimeout(focus);
        };
    }, [s.open, onClose]);
    // Cards being followed stay on screen even after the server stops listing them (it excludes SIs on their way).
    const cards = useMemo(() => {
        const listed = s.listing?.sis ?? [];
        const ids = new Set(listed.map(x => x.memberId));
        const followed = Object.values(s.pending).filter(p => !ids.has(p.si.memberId)).map(p => p.si);
        return [...listed, ...followed].sort((a, b) => a.name.localeCompare(b.name));
    }, [s.listing, s.pending]);
    const q = query.trim().toLowerCase();
    const shown = q
        ? cards.filter(c => c.name.toLowerCase().includes(q) || (c.jobTitle ?? '').toLowerCase().includes(q))
        : cards;
    const supervised = shown.filter(c => c.group === 'SUPERVISED');
    const available = shown.filter(c => c.group === 'AVAILABLE');
    const locked = shown.filter(c => c.group === 'LOCKED');
    const followedIds = new Set(Object.keys(s.pending));
    const present = (s.listing?.present ?? []).filter(p => !followedIds.has(p.memberId)
        || s.pending[p.memberId]?.phase === 'failed');
    if (!s.open) {
        return null;
    }
    const title = s.listing?.meeting?.title;
    let index = 0;
    return (React.createElement(React.Fragment, null,
        React.createElement("style", null, SI_INVITE_CSS),
        React.createElement("div", { "aria-labelledby": 'ksi-heading', className: 'ksi-panel', ref: panelRef, role: 'dialog', tabIndex: -1 },
            React.createElement("header", { className: 'ksi-head' },
                React.createElement("div", { "aria-hidden": 'true', className: 'ksi-badge' },
                    React.createElement(Icon, { size: 20, src: IconAI })),
                React.createElement("div", { className: 'ksi-head-text' },
                    React.createElement("h2", { className: 'ksi-heading', id: 'ksi-heading' }, w('siInvite.title')),
                    React.createElement("p", { className: 'ksi-sub' }, title ? w('siInvite.subtitle', { meeting: title }) : w('siInvite.subtitleNoTitle'))),
                React.createElement("button", { "aria-label": w('siInvite.close'), className: 'ksi-close', onClick: onClose, type: 'button' },
                    React.createElement(Icon, { size: 18, src: IconCloseLarge }))),
            cards.length > 4 && (React.createElement("label", { className: 'ksi-search' },
                React.createElement(Icon, { size: 16, src: IconSearch }),
                React.createElement("input", { "aria-label": w('siInvite.search'), onChange: onQuery, placeholder: w('siInvite.search'), ref: searchRef, type: 'search', value: query }))),
            React.createElement("div", { className: 'ksi-body' },
                !s.listing && s.loading && (React.createElement("ul", { "aria-label": w('siInvite.loading'), className: 'ksi-list' }, [0, 1, 2].map(i => (React.createElement("li", { className: 'ksi-card ksi-skeleton', key: i },
                    React.createElement("div", { className: 'ksi-row' },
                        React.createElement("div", { className: 'ksi-sk-face' }),
                        React.createElement("div", { className: 'ksi-who' },
                            React.createElement("div", { className: 'ksi-sk-line' }),
                            React.createElement("div", { className: 'ksi-sk-line ksi-sk-short' })))))))),
                s.listing && cards.length === 0 && (React.createElement("div", { className: 'ksi-empty' },
                    React.createElement("div", { "aria-hidden": 'true', className: 'ksi-empty-art' },
                        React.createElement(Icon, { size: 28, src: IconAI })),
                    present.length > 0 ? (React.createElement("p", { className: 'ksi-empty-title' }, w('siInvite.emptyAllHere'))) : (React.createElement(React.Fragment, null,
                        React.createElement("p", { className: 'ksi-empty-title' }, w('siInvite.emptyNone')),
                        React.createElement("p", { className: 'ksi-empty-hint' }, w('siInvite.emptyNoneHint')))))),
                s.listing && cards.length > 0 && shown.length === 0 && (React.createElement("div", { className: 'ksi-empty' },
                    React.createElement("p", { className: 'ksi-empty-title' }, w('siInvite.emptySearch', { q: query.trim() })))),
                supervised.length > 0 && (React.createElement("section", null,
                    React.createElement("div", { className: 'ksi-group-head' },
                        React.createElement("h3", { className: 'ksi-group-title' }, w('siInvite.groupSupervised')),
                        React.createElement("span", { className: 'ksi-count' }, supervised.length)),
                    React.createElement("p", { className: 'ksi-group-hint' }, w('siInvite.groupSupervisedHint')),
                    React.createElement("ul", { className: 'ksi-list' }, supervised.map(si => (React.createElement(SiCard, { index: index++, key: si.memberId, pending: s.pending[si.memberId], si: si, w: w })))))),
                available.length > 0 && (React.createElement("section", null,
                    React.createElement("div", { className: 'ksi-group-head' },
                        React.createElement("h3", { className: 'ksi-group-title' }, w('siInvite.groupAvailable')),
                        React.createElement("span", { className: 'ksi-count' }, available.length)),
                    React.createElement("p", { className: 'ksi-group-hint' }, w('siInvite.groupAvailableHint')),
                    React.createElement("ul", { className: 'ksi-list' }, available.map(si => (React.createElement(SiCard, { index: index++, key: si.memberId, pending: s.pending[si.memberId], si: si, w: w })))))),
                locked.length > 0 && (React.createElement("section", null,
                    React.createElement("div", { className: 'ksi-group-head' },
                        React.createElement("h3", { className: 'ksi-group-title' }, w('siInvite.groupLocked')),
                        React.createElement("span", { className: 'ksi-count' }, locked.length)),
                    React.createElement("p", { className: 'ksi-group-hint' }, w('siInvite.groupLockedHint')),
                    React.createElement("ul", { className: 'ksi-list' }, locked.map(si => (React.createElement(SiCard, { index: index++, key: si.memberId, pending: s.pending[si.memberId], si: si, w: w })))))),
                present.length > 0 && React.createElement(PresentStrip, { present: present, w: w }),
                s.error && s.listing && React.createElement("p", { className: 'ksi-soft-error', role: 'status' }, w('siInvite.loadError'))),
            React.createElement("footer", { className: 'ksi-foot' }, w('siInvite.footer')))));
}
